import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import type {
	RenderFunction,
	UseElementOptions,
	UseInlineNearElementParams,
	UseMountToHtmlElementOptions,
	UsePortalNearElementParams,
} from './types';

/**
 * Creates a portal to mount React components to HTML elements found by CSS selectors.
 *
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.selectors] - CSS selector to find the target HTML element
 * @param {'portal' | 'inline'} [options.as='portal'] - How to render the content: 'portal' creates a portal, 'inline' inserts into normal React flow
 * @param {'after' | 'before' | 'inside'} [options.position='after'] - Where to position the content relative to the target element
 * @param {Element | Document} [options.observeRoot] - Root element to observe for mutations. When provided, enables MutationObserver to watch for dynamic elements. When not provided, only performs a one-time query.
 *
 * @returns {Function} A render function that creates a portal to mount children after the target element or returns children inline
 *
 * @example
 * ```tsx
 * // Portal mode (default)
 * const MyComponent = () => {
 *   const renderPortal = useMountToHtmlElement({ selectors: '#my-target-element' });
 *
 *   return renderPortal(
 *     <div>This will be mounted after the element with id="my-target-element"</div>
 *   );
 * };
 *
 * // Watching for dynamic elements
 * const MyDynamicComponent = () => {
 *   const renderPortal = useMountToHtmlElement({
 *     selectors: '#dynamic-element',
 *     observeRoot: document.body, // Wait for element to appear
 *   });
 *
 *   return renderPortal(
 *     <div>This will mount once #dynamic-element appears in the DOM</div>
 *   );
 * };
 *
 * // Inline mode
 * const MyInlineComponent = () => {
 *   const renderInline = useMountToHtmlElement({ selectors: '#my-target-element', as: 'inline' });
 *
 *   return renderInline(
 *     <div>This will render inline in the React tree</div>
 *   );
 * };
 *
 * // Custom position
 * const MyBeforeComponent = () => {
 *   const renderBefore = useMountToHtmlElement({
 *     selectors: '#my-target-element',
 *     position: 'before'
 *   });
 *
 *   return renderBefore(
 *     <div>This will be mounted before the target element</div>
 *   );
 * };
 * ```
 *
 * @accessibility
 * This approach has several accessibility problems with are also present in legacy Spotlight and Engagement Platform:
 * - **Focus Management**: Portaled content breaks natural tab order and focus flow
 * - **Screen Reader Navigation**: Screen readers may not properly announce the relationship
 *   between the portal content and its context
 * - **Keyboard Navigation**: Users navigating by keyboard may lose their place when focus
 *   jumps unexpectedly to portaled content
 * - **Semantic Relationships**: ARIA relationships and semantic structure can be broken
 *   when content is moved out of its natural DOM position
 * - **Live Regions**: Dynamic content updates may not be properly announced to
 *   assistive technologies
 * - **Context Loss**: Content loses its semantic context when moved to a different
 *   part of the DOM tree
 */
export const useMountToHtmlElement = (params?: UseMountToHtmlElementOptions): RenderFunction => {
	const { as, selectors, position = 'after', observeRoot } = params ?? {};

	const targetElement = useElement({ selectors, observeRoot });
	const renderPortal = usePortalNearElement({ targetElement, position });
	const renderInline = useInlineNearElement({ targetElement, position });

	if (as === 'inline') {
		return renderInline;
	}

	return renderPortal;
};

const useElement = ({ selectors, observeRoot }: UseElementOptions): Element | undefined => {
	const knownEls = useRef<Record<string, Element | undefined>>({});
	const [el, setEl] = useState<Element | undefined>();

	useEffect(() => {
		if (typeof globalThis.document === 'undefined' || !selectors) {
			setEl(undefined);

			return;
		}

		if (knownEls.current?.[selectors]) {
			setEl(knownEls.current[selectors]);

			return;
		}

		const element = globalThis.document.querySelector(selectors) ?? undefined;

		knownEls.current[selectors] = element;

		setEl(element);

		if (observeRoot) {
			const observer = new MutationObserver(() => {
				const foundElement = globalThis.document.querySelector(selectors);
				if (foundElement) {
					setEl(foundElement);
					observer.disconnect();
				}
			});

			observer.observe(observeRoot, {
				childList: true,
				subtree: true,
			});

			return () => {
				observer.disconnect();
			};
		}

		return;
	}, [selectors, observeRoot]);

	return el;
};

const usePortalNearElement = ({
	targetElement,
	position = 'after',
}: UsePortalNearElementParams) => {
	const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}

		if (!targetElement) {
			setPortalContainer(null);

			return;
		}

		const container = globalThis?.document?.createElement('div');

		container.style.position = 'absolute';

		if (position === 'after') {
			targetElement.parentNode?.insertBefore(container, targetElement.nextSibling);
		} else if (position === 'before') {
			targetElement.parentNode?.insertBefore(container, targetElement);
		} else if (position === 'inside') {
			targetElement.appendChild(container);
		}

		setPortalContainer(container);

		return () => {
			container.remove();
		};
	}, [targetElement, position]);

	const renderPortal = (children: React.ReactNode) => {
		return portalContainer ? createPortal(children, portalContainer) : null;
	};

	return renderPortal;
};

const useInlineNearElement = ({
	targetElement,
	position = 'after',
}: UseInlineNearElementParams) => {
	const [inlineContainer, setInlineContainer] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}

		if (!targetElement) {
			setInlineContainer(null);

			return;
		}

		const container = globalThis?.document?.createElement('div');

		if (position === 'after') {
			targetElement.parentNode?.insertBefore(container, targetElement.nextSibling);
		} else if (position === 'before') {
			targetElement.parentNode?.insertBefore(container, targetElement);
		} else if (position === 'inside') {
			targetElement.appendChild(container);
		}

		setInlineContainer(container);

		return () => {
			container.remove();
		};
	}, [targetElement, position]);

	const renderInline = (children: React.ReactNode) => {
		return inlineContainer ? createPortal(children, inlineContainer) : null;
	};

	return renderInline;
};
